import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FaCog, FaArrowLeft, FaSave, FaDatabase, FaShieldAlt, FaEnvelope } from 'react-icons/fa';

const SystemSettings = () => {
    const [loading, setLoading] = useState(false);
    const [settings, setSettings] = useState({
        siteName: 'Ojoto Union North America',
        siteDescription: 'A vibrant community connecting Ojoto indigenes across North America',
        maintenanceMode: false,
        allowRegistrations: true,
        requireEmailVerification: true,
        sessionTimeout: 30,
        maxFileUploadSize: 10
    });

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSettings({
            ...settings,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleSave = async () => {
        setLoading(true);
        // TODO: Implement API call to save settings
        setTimeout(() => {
            toast.success('Settings saved successfully!');
            setLoading(false);
        }, 1000);
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <div className="max-w-4xl mx-auto px-6 py-8">
                <Link to="/dashboard?tab=settings" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
                    <FaArrowLeft className="mr-2" /> Back to Dashboard
                </Link>

                <div className="bg-white rounded-xl shadow-lg p-8">
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-6">
                        <FaCog className="text-primary-600" />
                        System Settings
                    </h1>

                    <div className="space-y-8">
                        {/* General Settings */}
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <FaDatabase /> General Settings
                            </h2>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Site Name
                                    </label>
                                    <input
                                        type="text"
                                        name="siteName"
                                        value={settings.siteName}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Site Description
                                    </label>
                                    <textarea
                                        name="siteDescription"
                                        value={settings.siteDescription}
                                        onChange={handleChange}
                                        rows={3}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Security Settings */}
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <FaShieldAlt /> Security Settings
                            </h2>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                    <div>
                                        <label className="font-medium text-gray-700">Maintenance Mode</label>
                                        <p className="text-sm text-gray-500">Temporarily disable site access</p>
                                    </div>
                                    <input
                                        type="checkbox"
                                        name="maintenanceMode"
                                        checked={settings.maintenanceMode}
                                        onChange={handleChange}
                                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                </div>
                                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                    <div>
                                        <label className="font-medium text-gray-700">Allow New Registrations</label>
                                        <p className="text-sm text-gray-500">Enable/disable user registration</p>
                                    </div>
                                    <input
                                        type="checkbox"
                                        name="allowRegistrations"
                                        checked={settings.allowRegistrations}
                                        onChange={handleChange}
                                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                </div>
                                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                    <div>
                                        <label className="font-medium text-gray-700">Require Email Verification</label>
                                        <p className="text-sm text-gray-500">Users must verify email before login</p>
                                    </div>
                                    <input
                                        type="checkbox"
                                        name="requireEmailVerification"
                                        checked={settings.requireEmailVerification}
                                        onChange={handleChange}
                                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Session Timeout (minutes)
                                    </label>
                                    <input
                                        type="number"
                                        name="sessionTimeout"
                                        value={settings.sessionTimeout}
                                        onChange={handleChange}
                                        min="5"
                                        max="120"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* File Upload Settings */}
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <FaEnvelope /> File Upload Settings
                            </h2>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Max File Upload Size (MB)
                                </label>
                                <input
                                    type="number"
                                    name="maxFileUploadSize"
                                    value={settings.maxFileUploadSize}
                                    onChange={handleChange}
                                    min="1"
                                    max="100"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        {/* Save Button */}
                        <div className="pt-6 border-t border-gray-200">
                            <button
                                onClick={handleSave}
                                disabled={loading}
                                className="w-full px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-500 text-gray-900 font-bold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                <FaSave /> {loading ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SystemSettings;

